import { useState, useEffect, useMemo, useCallback, useRef, FormEvent, ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
	PencilIcon,
	PhoneIcon,
	EnvelopeIcon,
	ExclamationTriangleIcon,
	EyeIcon,
	EyeSlashIcon,
	CheckIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
	ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader, Badge, Button, Modal, Spinner } from '../../components/ui';
import { staffProfileApi, authApi, ApiError } from '../../api';
import type { ServiceTask } from '../../types/appointments';
import useStaffProfileStore from '../../stores/staffProfileStore';
import useAuthStore from '../../stores/authStore';
import useNotificationsStore from '../../stores/notificationsStore';
import { formatInfoRequestDate } from '../../utils/infoRequest';

// Format ISO date string to readable format
const formatDate = (dateStr: string): string => {
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return dateStr;
	}
};

// Get initials from name
const getInitials = (firstName: string, lastName: string): string => {
	return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Grade badge styles
const GRADE_STYLES: Record<string, string> = {
	ENTRY: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
	EXPERT: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
	PRO: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
	MASTER: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
};

// Password requirements (reused from ChangePassword page)
const PASSWORD_REQUIREMENTS = {
	length: { regex: /.{8,}/, label: 'Minimum 8 characters' },
	uppercase: { regex: /[A-Z]/, label: 'At least 1 uppercase letter (A-Z)' },
	lowercase: { regex: /[a-z]/, label: 'At least 1 lowercase letter (a-z)' },
	number: { regex: /[0-9]/, label: 'At least 1 number (0-9)' },
	special: {
		regex: /[!@#$%^&*(),.?":{}|<>]/,
		label: 'At least 1 special character (!@#$%^&*)',
	},
};

type PasswordStrength = 'not-set' | 'weak' | 'medium' | 'strong';

// ─── PersonalInfoModal ──────────────────────────────────────────

interface PersonalInfoModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialData: { firstName: string; lastName: string; phone: string; email: string };
	onSaved: () => void;
}

const PersonalInfoModal = ({ isOpen, onClose, initialData, onSaved }: PersonalInfoModalProps) => {
	const [firstName, setFirstName] = useState(initialData.firstName);
	const [lastName, setLastName] = useState(initialData.lastName);
	const [phone, setPhone] = useState(initialData.phone);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setFirstName(initialData.firstName);
			setLastName(initialData.lastName);
			setPhone(initialData.phone);
		}
	}, [isOpen, initialData]);

	const isValid = firstName.trim() && lastName.trim() && phone.trim();

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!isValid || isSaving) return;

		setIsSaving(true);
		try {
			await staffProfileApi.updatePersonalInfo({
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				phone: phone.trim(),
			});
			toast.success('Personal information updated successfully!');
			onSaved();
			onClose();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to update personal information');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Edit Personal Information" size="md">
			<form onSubmit={handleSubmit}>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							First Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							value={firstName}
							onChange={e => setFirstName(e.target.value)}
							className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Last Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							value={lastName}
							onChange={e => setLastName(e.target.value)}
							className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Phone <span className="text-red-500">*</span>
						</label>
						<input
							type="tel"
							value={phone}
							onChange={e => setPhone(e.target.value)}
							className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Email
						</label>
						<input
							type="email"
							value={initialData.email}
							disabled
							className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
						/>
					</div>
				</div>
				<div className="flex justify-end gap-3 mt-6">
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						disabled={!isValid}
						isLoading={isSaving}
					>
						Save Changes
					</Button>
				</div>
			</form>
		</Modal>
	);
};

// ─── Shared helpers ─────────────────────────────────────────────
import { getUniqueTasks, formatTaskType } from '../../utils/taskHelpers';
import type { UniqueTask } from '../../utils/taskHelpers';

// ─── ServicesTasksModal ─────────────────────────────────────────

interface ServicesTasksModalProps {
	isOpen: boolean;
	onClose: () => void;
	allServices: ServiceTask[];
	isLoadingServices: boolean;
	selectedTaskIds: number[];
	onSaved: () => void;
}

const ServicesTasksModal = ({
	isOpen,
	onClose,
	allServices,
	isLoadingServices,
	selectedTaskIds,
	onSaved,
}: ServicesTasksModalProps) => {
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [expandedServiceId, setExpandedServiceId] = useState<number | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [filter, setFilter] = useState<'all' | 'selected'>('all');
	const [isSaving, setIsSaving] = useState(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [debouncedQuery, setDebouncedQuery] = useState('');

	// Reset state on open
	useEffect(() => {
		if (!isOpen) return;
		setSelected(new Set(selectedTaskIds));
		setExpandedServiceId(null);
		setSearchQuery('');
		setDebouncedQuery('');
		setFilter('all');
	}, [isOpen, selectedTaskIds]);

	// Debounced search
	useEffect(() => {
		if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		searchTimerRef.current = setTimeout(
			() => setDebouncedQuery(searchQuery.trim().toLowerCase()),
			180
		);
		return () => {
			if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		};
	}, [searchQuery]);

	// Build per-service unique tasks map
	const serviceTasksMap = useMemo(() => {
		const map = new Map<number, UniqueTask[]>();
		for (const svc of allServices) {
			map.set(svc.id, getUniqueTasks(svc));
		}
		return map;
	}, [allServices]);

	// Filter + search
	const filteredServices = useMemo(() => {
		let list = allServices;

		if (filter === 'selected') {
			list = list.filter(svc => {
				const tasks = serviceTasksMap.get(svc.id) ?? [];
				return tasks.some(t => selected.has(t.serviceTaskId));
			});
		}

		if (debouncedQuery) {
			list = list.filter(svc => {
				const tasks = serviceTasksMap.get(svc.id) ?? [];
				return (
					svc.serviceName.toLowerCase().includes(debouncedQuery) ||
					tasks.some(t => t.taskType.toLowerCase().includes(debouncedQuery))
				);
			});
		}

		return list;
	}, [allServices, filter, debouncedQuery, serviceTasksMap, selected]);

	// Counts
	const selectedServiceCount = useMemo(() => {
		let count = 0;
		for (const svc of allServices) {
			const tasks = serviceTasksMap.get(svc.id) ?? [];
			if (tasks.some(t => selected.has(t.serviceTaskId))) count++;
		}
		return count;
	}, [allServices, serviceTasksMap, selected]);

	const selectedTaskCount = selected.size;

	// Toggle a single task
	const toggleTask = useCallback((serviceTaskId: number) => {
		setSelected(prev => {
			const next = new Set(prev);
			if (next.has(serviceTaskId)) next.delete(serviceTaskId);
			else next.add(serviceTaskId);
			return next;
		});
	}, []);

	// Select all / deselect all tasks for a service
	const selectAllForService = useCallback(
		(serviceId: number) => {
			const tasks = serviceTasksMap.get(serviceId) ?? [];
			setSelected(prev => {
				const next = new Set(prev);
				tasks.forEach(t => next.add(t.serviceTaskId));
				return next;
			});
		},
		[serviceTasksMap]
	);

	const deselectAllForService = useCallback(
		(serviceId: number) => {
			const tasks = serviceTasksMap.get(serviceId) ?? [];
			setSelected(prev => {
				const next = new Set(prev);
				tasks.forEach(t => next.delete(t.serviceTaskId));
				return next;
			});
		},
		[serviceTasksMap]
	);

	// Toggle expand a service card
	const toggleExpand = useCallback((serviceId: number) => {
		setExpandedServiceId(prev => (prev === serviceId ? null : serviceId));
	}, []);

	// Save
	const handleSave = async () => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			await staffProfileApi.updateTasks(Array.from(selected));
			toast.success('Services & tasks updated successfully!');
			onSaved();
			onClose();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to update tasks');
		} finally {
			setIsSaving(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-5 bg-black/60 backdrop-blur-sm"
			onClick={e => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div
				className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-[820px] max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300 overflow-hidden"
				onClick={e => e.stopPropagation()}
			>
				{/* Header */}
				<div className="px-5 sm:px-7 pt-5 pb-4 border-b border-gray-100 dark:border-slate-700 flex items-start justify-between">
					<div>
						<h2 className="text-lg font-bold text-gray-900 dark:text-white">
							Select Services & Tasks
						</h2>
						<p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
							Pick the services you perform, then choose your tasks for each one
						</p>
					</div>
					<button
						onClick={onClose}
						className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors cursor-pointer flex-shrink-0"
					>
						<XMarkIcon className="w-4 h-4" />
					</button>
				</div>

				{/* Search */}
				<div className="px-5 sm:px-7 py-3 border-b border-gray-100 dark:border-slate-700">
					<div className="flex items-center gap-2.5 bg-gray-50 dark:bg-slate-900 border-2 border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2.5 focus-within:border-[#667eea] dark:focus-within:border-indigo-400 focus-within:bg-white dark:focus-within:bg-slate-800 transition-all">
						<MagnifyingGlassIcon className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
						<input
							ref={searchInputRef}
							type="text"
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							placeholder="Search services..."
							className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
						/>
						{searchQuery && (
							<button
								onClick={() => {
									setSearchQuery('');
									searchInputRef.current?.focus();
								}}
								className="text-gray-400 hover:text-[#667eea] cursor-pointer"
							>
								<XMarkIcon className="w-4 h-4" />
							</button>
						)}
					</div>
				</div>

				{/* Filter Tabs */}
				<div className="flex gap-2 px-5 sm:px-7 py-3 border-b border-gray-100 dark:border-slate-700">
					{(['all', 'selected'] as const).map(f => {
						const isActive = filter === f;
						const count = f === 'all' ? allServices.length : selectedServiceCount;
						return (
							<button
								key={f}
								onClick={() => setFilter(f)}
								className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border-2 transition-all cursor-pointer ${
									isActive
										? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] border-transparent text-white'
										: 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-[#667eea] hover:text-[#667eea]'
								}`}
							>
								{f === 'all' ? 'All' : 'Selected'}
								<span
									className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-xs ml-1.5 px-1 ${
										isActive
											? 'bg-white/25'
											: 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
									}`}
								>
									{count}
								</span>
							</button>
						);
					})}
				</div>

				{/* Body — Service List */}
				<div className="flex-1 overflow-y-auto px-5 sm:px-7 py-4">
					{isLoadingServices ? (
						<div className="flex items-center justify-center py-16">
							<Spinner size="lg" className="text-[#667eea]" />
						</div>
					) : filteredServices.length === 0 ? (
						<div className="text-center py-12 text-gray-400 dark:text-slate-500">
							<MagnifyingGlassIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
							<p className="text-sm">No services found</p>
						</div>
					) : (
						<div className="space-y-2.5">
							{filteredServices.map(svc => {
								const tasks = serviceTasksMap.get(svc.id) ?? [];
								const selectedCountForSvc = tasks.filter(t =>
									selected.has(t.serviceTaskId)
								).length;
								const isExpanded = expandedServiceId === svc.id;
								const hasSelection = selectedCountForSvc > 0;

								return (
									<div
										key={svc.id}
										className={`border-2 rounded-xl overflow-hidden transition-all ${
											hasSelection
												? 'border-[#667eea] dark:border-indigo-400 shadow-sm shadow-indigo-100 dark:shadow-none'
												: 'border-gray-200 dark:border-slate-600'
										}`}
									>
										{/* Service Header */}
										<div
											className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
											onClick={() => toggleExpand(svc.id)}
										>
											{/* Toggle checkbox */}
											<div
												className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
													hasSelection
														? 'bg-gradient-to-br from-[#667eea] to-[#764ba2] border-[#667eea]'
														: 'border-gray-300 dark:border-slate-500'
												}`}
											>
												{hasSelection && (
													<CheckIcon className="w-3.5 h-3.5 text-white" />
												)}
											</div>

											{/* Service info */}
											<div className="flex-1 min-w-0">
												<div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
													{svc.serviceName}
												</div>
												<div className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
													{tasks.length} task
													{tasks.length !== 1 ? 's' : ''} available
												</div>
											</div>

											{/* Task count badge */}
											<span
												className={`text-xs px-2 py-0.5 rounded-md font-semibold flex-shrink-0 ${
													hasSelection
														? 'bg-indigo-50 dark:bg-indigo-900/40 text-[#667eea] dark:text-indigo-300'
														: 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
												}`}
											>
												{selectedCountForSvc}/{tasks.length}
											</span>

											{/* Expand chevron */}
											<ChevronDownIcon
												className={`w-5 h-5 text-gray-400 dark:text-slate-500 transition-transform flex-shrink-0 ${
													isExpanded ? 'rotate-180 text-[#667eea]' : ''
												}`}
											/>
										</div>

										{/* Tasks Panel (expandable) */}
										<div
											className={`overflow-hidden transition-all duration-300 ease-in-out ${
												isExpanded ? 'max-h-[500px]' : 'max-h-0'
											}`}
										>
											<div className="px-4 py-3 bg-gray-50 dark:bg-slate-900 border-t border-gray-200 dark:border-slate-600">
												{/* Quick actions */}
												<div className="flex items-center justify-between mb-3">
													<span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">
														Available Tasks
													</span>
													<div className="flex gap-1">
														<button
															onClick={e => {
																e.stopPropagation();
																selectAllForService(svc.id);
															}}
															className="text-xs font-semibold text-[#667eea] dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-800 px-2 py-1 rounded-md transition-colors cursor-pointer"
														>
															Select All
														</button>
														<button
															onClick={e => {
																e.stopPropagation();
																deselectAllForService(svc.id);
															}}
															className="text-xs font-semibold text-[#667eea] dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-800 px-2 py-1 rounded-md transition-colors cursor-pointer"
														>
															Deselect All
														</button>
													</div>
												</div>

												{/* Task chips */}
												<div className="flex flex-wrap gap-2">
													{tasks.map(task => {
														const isSelected = selected.has(
															task.serviceTaskId
														);
														return (
															<button
																key={task.serviceTaskId}
																onClick={e => {
																	e.stopPropagation();
																	toggleTask(task.serviceTaskId);
																}}
																className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer select-none ${
																	isSelected
																		? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] border-transparent text-white shadow-md shadow-indigo-200 dark:shadow-none'
																		: 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:border-[#667eea] hover:bg-indigo-50 dark:hover:bg-slate-700 hover:-translate-y-0.5'
																}`}
															>
																{/* Mini checkbox */}
																<span
																	className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all ${
																		isSelected
																			? 'bg-white/25 border-white/40'
																			: 'border-gray-300 dark:border-slate-500'
																	}`}
																>
																	{isSelected && (
																		<CheckIcon className="w-2.5 h-2.5 text-white" />
																	)}
																</span>
																{formatTaskType(task.taskType)}
															</button>
														);
													})}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="px-5 sm:px-7 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex flex-col sm:flex-row items-center justify-between gap-3">
					<div className="text-sm text-gray-500 dark:text-slate-400">
						<strong className="text-[#667eea] font-bold">{selectedServiceCount}</strong>{' '}
						service{selectedServiceCount !== 1 ? 's' : ''},{' '}
						<strong className="text-[#667eea] font-bold">{selectedTaskCount}</strong>{' '}
						task{selectedTaskCount !== 1 ? 's' : ''} selected
					</div>
					<div className="flex gap-3 w-full sm:w-auto">
						<button
							onClick={onClose}
							className="flex-1 sm:flex-initial px-5 py-2.5 rounded-lg border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-200 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-slate-600 transition-all cursor-pointer"
						>
							Cancel
						</button>
						<button
							onClick={handleSave}
							disabled={isSaving}
							className="flex-1 sm:flex-initial px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white font-semibold text-sm shadow-md shadow-indigo-200 dark:shadow-none hover:opacity-90 hover:-translate-y-0.5 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isSaving ? (
								<Spinner size="sm" className="text-white mx-auto" />
							) : (
								'Save Changes'
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

// ─── ChangePasswordModal ────────────────────────────────────────

interface ChangePasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordModalProps) => {
	const { refreshSession } = useAuthStore();
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
			setShowCurrentPassword(false);
			setShowNewPassword(false);
			setShowConfirmPassword(false);
		}
	}, [isOpen]);

	const requirementsMet = useMemo(() => {
		return Object.entries(PASSWORD_REQUIREMENTS).reduce(
			(acc, [key, { regex }]) => {
				acc[key as keyof typeof PASSWORD_REQUIREMENTS] = regex.test(newPassword);
				return acc;
			},
			{} as Record<keyof typeof PASSWORD_REQUIREMENTS, boolean>
		);
	}, [newPassword]);

	const metCount = useMemo(
		() => Object.values(requirementsMet).filter(Boolean).length,
		[requirementsMet]
	);

	const passwordStrength: PasswordStrength = useMemo(() => {
		if (newPassword.length === 0) return 'not-set';
		if (metCount <= 2) return 'weak';
		if (metCount <= 4) return 'medium';
		return 'strong';
	}, [newPassword.length, metCount]);

	const allRequirementsMet = metCount === Object.keys(PASSWORD_REQUIREMENTS).length;
	const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
	const isFormValid = currentPassword.length > 0 && allRequirementsMet && passwordsMatch;

	const strengthColors = {
		'not-set': 'bg-gray-200 dark:bg-gray-600',
		weak: 'bg-red-500',
		medium: 'bg-amber-500',
		strong: 'bg-emerald-500',
	};

	const strengthWidths = {
		'not-set': 'w-0',
		weak: 'w-1/3',
		medium: 'w-2/3',
		strong: 'w-full',
	};

	const strengthLabels = {
		'not-set': '',
		weak: 'Weak',
		medium: 'Medium',
		strong: 'Strong',
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!isFormValid || isSubmitting) return;

		setIsSubmitting(true);
		try {
			const response = await authApi.changePassword({
				currentPassword,
				newPassword,
				confirmPassword,
			});

			if (response.success) {
				refreshSession(response.token, {
					name: response.name,
					image: response.image,
					passwordChangeRequired: false,
				});
				toast.success('Password changed successfully!');
				onClose();
			} else {
				toast.error(response.message || 'Failed to change password');
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to change password');
		} finally {
			setIsSubmitting(false);
		}
	};

	const inputClasses =
		'w-full pl-3.5 pr-10 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors';

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Change Password" size="md">
			<form onSubmit={handleSubmit}>
				{/* Current Password */}
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Current Password <span className="text-red-500">*</span>
					</label>
					<div className="relative">
						<input
							type={showCurrentPassword ? 'text' : 'password'}
							value={currentPassword}
							onChange={e => setCurrentPassword(e.target.value)}
							placeholder="Enter current password"
							className={inputClasses}
							autoComplete="current-password"
						/>
						<button
							type="button"
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
							onClick={() => setShowCurrentPassword(!showCurrentPassword)}
						>
							{showCurrentPassword ? (
								<EyeSlashIcon className="w-5 h-5" />
							) : (
								<EyeIcon className="w-5 h-5" />
							)}
						</button>
					</div>
				</div>

				{/* New Password */}
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						New Password <span className="text-red-500">*</span>
					</label>
					<div className="relative">
						<input
							type={showNewPassword ? 'text' : 'password'}
							value={newPassword}
							onChange={e => setNewPassword(e.target.value)}
							placeholder="Enter new password"
							className={inputClasses}
							autoComplete="new-password"
						/>
						<button
							type="button"
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
							onClick={() => setShowNewPassword(!showNewPassword)}
						>
							{showNewPassword ? (
								<EyeSlashIcon className="w-5 h-5" />
							) : (
								<EyeIcon className="w-5 h-5" />
							)}
						</button>
					</div>

					{/* Requirements */}
					{newPassword.length > 0 && (
						<div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
							<ul className="space-y-1.5">
								{Object.entries(PASSWORD_REQUIREMENTS).map(([key, { label }]) => {
									const isMet =
										requirementsMet[key as keyof typeof PASSWORD_REQUIREMENTS];
									return (
										<li
											key={key}
											className={`flex items-center gap-2 text-xs ${isMet ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}
										>
											{isMet ? (
												<CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
											) : (
												<span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
													<span className="w-1.5 h-1.5 rounded-full bg-current" />
												</span>
											)}
											{label}
										</li>
									);
								})}
							</ul>
							{/* Strength meter */}
							<div className="mt-3">
								<div className="flex justify-between text-xs mb-1">
									<span className="text-gray-500 dark:text-gray-400">
										Strength
									</span>
									<span
										className={`font-medium ${
											passwordStrength === 'weak'
												? 'text-red-500'
												: passwordStrength === 'medium'
													? 'text-amber-500'
													: passwordStrength === 'strong'
														? 'text-emerald-500'
														: 'text-gray-400'
										}`}
									>
										{strengthLabels[passwordStrength]}
									</span>
								</div>
								<div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
									<div
										className={`h-full rounded-full transition-all duration-300 ${strengthWidths[passwordStrength]} ${strengthColors[passwordStrength]}`}
									/>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Confirm Password */}
				<div className="mb-6">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Confirm Password <span className="text-red-500">*</span>
					</label>
					<div className="relative">
						<input
							type={showConfirmPassword ? 'text' : 'password'}
							value={confirmPassword}
							onChange={e => setConfirmPassword(e.target.value)}
							placeholder="Confirm new password"
							className={`${inputClasses} ${
								confirmPassword.length > 0
									? passwordsMatch
										? 'border-emerald-500 focus:border-emerald-500'
										: 'border-red-500 focus:border-red-500'
									: ''
							}`}
							autoComplete="new-password"
						/>
						<button
							type="button"
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
						>
							{showConfirmPassword ? (
								<EyeSlashIcon className="w-5 h-5" />
							) : (
								<EyeIcon className="w-5 h-5" />
							)}
						</button>
					</div>
					{confirmPassword.length > 0 && !passwordsMatch && (
						<p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
							<ExclamationTriangleIcon className="w-3.5 h-3.5" />
							Passwords do not match
						</p>
					)}
					{passwordsMatch && (
						<p className="mt-1.5 text-xs text-emerald-500 flex items-center gap-1">
							<CheckIcon className="w-3.5 h-3.5" />
							Passwords match
						</p>
					)}
				</div>

				<div className="flex justify-end gap-3">
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						disabled={!isFormValid}
						isLoading={isSubmitting}
					>
						Change Password
					</Button>
				</div>
			</form>
		</Modal>
	);
};

// ─── Info Card Component ────────────────────────────────────────

interface InfoCardProps {
	title: string;
	onEdit?: () => void;
	children: ReactNode;
}

const InfoCard = ({ title, onEdit, children }: InfoCardProps) => (
	<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-5 overflow-hidden">
		<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
			<h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
			{onEdit && (
				<button
					onClick={onEdit}
					className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-[#667eea] hover:text-[#667eea] transition-all cursor-pointer"
				>
					<PencilIcon className="w-3.5 h-3.5" />
					Edit
				</button>
			)}
		</div>
		<div className="px-5 py-5">{children}</div>
	</div>
);

// ─── Personal-Info Request Status ───────────────────────────────

/**
 * The durable record of the provider's personal-info request (AC6).
 *
 * The bell and dashboard alert are transient prompts; this is where a staff
 * member comes back to answer "did I already send this?". It's the only surface
 * that still says something useful once the request is COMPLETED.
 *
 * The submitted values themselves aren't shown — StaffProfile carries no SSN or
 * DOB field, so there's nothing to render back (not even a masked last-4).
 */
const InfoRequestRow = () => {
	const infoRequestState = useNotificationsStore(state => state.infoRequestState);
	const expiresOn = useNotificationsStore(state =>
		formatInfoRequestDate(state.infoRequest?.expiresOn ?? null)
	);
	const openInfoRequestModal = useNotificationsStore(state => state.openInfoRequestModal);

	if (infoRequestState === 'NONE') return null;

	const badge = {
		PENDING: <Badge variant="warning">Pending</Badge>,
		COMPLETED: <Badge variant="success">Submitted</Badge>,
		EXPIRED: <Badge variant="danger">Expired</Badge>,
	}[infoRequestState];

	const caption = {
		PENDING: expiresOn
			? `Requested by your administrator · Expires ${expiresOn}`
			: 'Requested by your administrator',
		COMPLETED: 'Your details are on file.',
		EXPIRED: 'Ask your administrator to send a new request.',
	}[infoRequestState];

	return (
		<div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
			<div>
				<span className="block text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
					Employment &amp; Payroll Details
				</span>
				<div className="flex items-center gap-2 flex-wrap">
					{badge}
					<span className="text-xs text-gray-500 dark:text-gray-400">{caption}</span>
				</div>
			</div>
			{infoRequestState === 'PENDING' && (
				<Button size="sm" onClick={openInfoRequestModal}>
					Complete Now
				</Button>
			)}
		</div>
	);
};

// ─── Info Grid Item ─────────────────────────────────────────────

interface InfoItemProps {
	label: string;
	value: ReactNode;
}

const InfoItem = ({ label, value }: InfoItemProps) => (
	<div>
		<span className="block text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
			{label}
		</span>
		<span className="text-sm font-medium text-gray-900 dark:text-white">{value || '-'}</span>
	</div>
);

// ─── Main Profile Page ──────────────────────────────────────────

const StaffProfile = () => {
	const { profile, isLoadingProfile, profileError, fetchProfile } = useStaffProfileStore();

	const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
	const [showServicesTasksModal, setShowServicesTasksModal] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);

	// Service tasks fetched at page level for both display and modal
	const [allServiceTasks, setAllServiceTasks] = useState<ServiceTask[]>([]);
	const [isLoadingServiceTasks, setIsLoadingServiceTasks] = useState(false);

	const fetchServiceTasks = useCallback(() => {
		setIsLoadingServiceTasks(true);
		staffProfileApi
			.getServiceTasks()
			.then(data => setAllServiceTasks(data))
			.catch(() => {})
			.finally(() => setIsLoadingServiceTasks(false));
	}, []);

	// Build display data: group selected tasks by service using service-tasks API data
	const selectedTasksByService = useMemo(() => {
		const selectedIds = new Set(profile?.selectedTaskIds ?? []);
		if (selectedIds.size === 0 || allServiceTasks.length === 0) return [];

		const result: { serviceName: string; taskTypes: UniqueTask[] }[] = [];
		for (const svc of allServiceTasks) {
			const uniqueTasks = getUniqueTasks(svc);
			const selectedTasks = uniqueTasks.filter(t => selectedIds.has(t.serviceTaskId));
			if (selectedTasks.length > 0) {
				result.push({ serviceName: svc.serviceName, taskTypes: selectedTasks });
			}
		}
		return result;
	}, [profile?.selectedTaskIds, allServiceTasks]);

	// Fetch profile and service tasks on mount
	useEffect(() => {
		if (!profile && !isLoadingProfile && !profileError) {
			fetchProfile();
		}
		fetchServiceTasks();
	}, [profile, isLoadingProfile, profileError, fetchProfile, fetchServiceTasks]);

	// Refresh both profile and service tasks after modal save
	const handleServiceTasksSaved = useCallback(async () => {
		await fetchProfile();
		fetchServiceTasks();
	}, [fetchProfile, fetchServiceTasks]);

	// Loading state
	if (isLoadingProfile && !profile) {
		return <AppLoader message="Loading profile" variant="fullscreen" />;
	}

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					View and manage your personal information
				</p>
			</div>

			{/* Error Alert */}
			{profileError && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Profile"
					message={profileError}
					actions={[{ label: 'Retry', onClick: fetchProfile, primary: true }]}
					onDismiss={() => {}}
				/>
			)}

			{profile && (
				<div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
					{/* Profile Card (Left Sidebar) */}
					<div className="lg:sticky lg:top-6 h-fit">
						<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-visible max-w-[400px] mx-auto lg:max-w-none">
							{/* Gradient Cover */}
							<div
								className="h-[100px] rounded-t-xl relative"
								style={{
									background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
								}}
							>
								{/* Avatar */}
								<div className="absolute -bottom-[50px] left-1/2 -translate-x-1/2 z-10">
									<div
										className="w-[100px] h-[100px] rounded-full flex items-center justify-center text-2xl font-bold text-white border-4 border-white dark:border-gray-800 shadow-lg"
										style={{
											background:
												'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
										}}
									>
										{getInitials(profile.firstName, profile.lastName)}
									</div>
								</div>
							</div>

							{/* Info */}
							<div className="pt-[60px] pb-6 px-6 text-center">
								<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
									{profile.firstName} {profile.lastName}
								</h2>

								{/* Badges */}
								<div className="flex justify-center gap-2 mb-5">
									<span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
										{profile.staffTypeName}
									</span>
									<span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
										{profile.statusName}
									</span>
								</div>

								{/* Contact */}
								<div className="space-y-3">
									{profile.phone && (
										<div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
											<PhoneIcon className="w-4 h-4 flex-shrink-0" />
											<span>{profile.phone}</span>
										</div>
									)}
									{profile.email && (
										<div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
											<EnvelopeIcon className="w-4 h-4 flex-shrink-0" />
											<span>{profile.email}</span>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Content Sections (Right Column) */}
					<div>
						{/* Personal Information */}
						<InfoCard
							title="Personal Information"
							onEdit={() => setShowPersonalInfoModal(true)}
						>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<InfoItem label="First Name" value={profile.firstName} />
								<InfoItem label="Last Name" value={profile.lastName} />
								<InfoItem label="Phone" value={profile.phone} />
								<InfoItem label="Email" value={profile.email} />
							</div>
							<InfoRequestRow />
						</InfoCard>

						{/* Employment Details */}
						<InfoCard title="Employment Details">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<InfoItem
									label="Hire Date"
									value={profile.hireDate ? formatDate(profile.hireDate) : '-'}
								/>
								<InfoItem label="Employment Type" value={profile.staffTypeName} />
								<InfoItem
									label="Grade"
									value={
										<span
											className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${GRADE_STYLES[profile.staffGrade] || GRADE_STYLES.ENTRY}`}
										>
											{profile.staffGradeName}
										</span>
									}
								/>
								<InfoItem
									label="Commission Rate"
									value={`${profile.commissionRate ?? 0}%`}
								/>
								<InfoItem label="Location" value={profile.locationName} />
							</div>
						</InfoCard>

						{/* Services & Tasks */}
						<InfoCard
							title="Services & Tasks"
							// TODO: Re-enable edit when ready
							// onEdit={() => setShowServicesTasksModal(true)}
						>
							{selectedTasksByService.length > 0 ? (
								<div className="space-y-3.5">
									{selectedTasksByService.map(({ serviceName, taskTypes }) => (
										<div key={serviceName}>
											<div className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5 flex items-center gap-2">
												{serviceName}
												<span className="text-xs text-gray-400 dark:text-slate-500 font-normal">
													({taskTypes.length} task
													{taskTypes.length !== 1 ? 's' : ''})
												</span>
											</div>
											<div className="flex flex-wrap gap-1.5 pl-4">
												{taskTypes.map(t => (
													<span
														key={t.serviceTaskId}
														className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-[#667eea] dark:text-indigo-300"
													>
														{formatTaskType(t.taskType)}
													</span>
												))}
											</div>
										</div>
									))}
								</div>
							) : (
								<p className="text-sm text-gray-400 dark:text-gray-500">
									No services selected yet. Click Edit to add your services and
									tasks.
								</p>
							)}
						</InfoCard>

						{/* Account Security */}
						<InfoCard title="Account Security">
							<div className="flex items-center justify-between">
								<div>
									<InfoItem label="Password" value="••••••••••••" />
								</div>
								<Button
									variant="secondary"
									size="sm"
									onClick={() => setShowPasswordModal(true)}
								>
									Change Password
								</Button>
							</div>
						</InfoCard>
					</div>
				</div>
			)}

			{/* Loading spinner when refreshing */}
			{isLoadingProfile && profile && (
				<div className="fixed inset-0 bg-black/10 flex items-center justify-center z-40 pointer-events-none">
					<Spinner size="lg" className="text-[#667eea]" />
				</div>
			)}

			{/* Modals */}
			{profile && (
				<>
					<PersonalInfoModal
						isOpen={showPersonalInfoModal}
						onClose={() => setShowPersonalInfoModal(false)}
						initialData={{
							firstName: profile.firstName,
							lastName: profile.lastName,
							phone: profile.phone,
							email: profile.email,
						}}
						onSaved={fetchProfile}
					/>
					<ServicesTasksModal
						isOpen={showServicesTasksModal}
						onClose={() => setShowServicesTasksModal(false)}
						allServices={allServiceTasks}
						isLoadingServices={isLoadingServiceTasks}
						selectedTaskIds={profile.selectedTaskIds ?? []}
						onSaved={handleServiceTasksSaved}
					/>
				</>
			)}
			<ChangePasswordModal
				isOpen={showPasswordModal}
				onClose={() => setShowPasswordModal(false)}
			/>
		</AppLayout>
	);
};

export default StaffProfile;
